import { atEnd, type CommentState } from './commentStore';

/**
 * One reconciliation request, fully specified. Steps carry their own bounds so
 * executing one never has to re-derive them from state, and no step can be
 * emitted for a window that cannot serve it.
 */
export type SyncStep =
  | { kind: 'initial'; around: number | null }
  | { kind: 'newer'; after: number }
  | { kind: 'summary' };

/**
 * What a reconciliation pass should fetch next, or `null` when there is nothing
 * to do. A closed topic is always `null`: a collapsed badge issues no HTTP at
 * all and takes its freshness from the channel.
 *
 * `tailHole` is positive proof from an out-of-order socket create that the tail
 * is incomplete. It earns an `after` even though the fresher snapshot that
 * arrived with it says the window is no longer at the end.
 */
export function planSync(state: CommentState, tailHole: boolean): SyncStep | null {
  if (!state.open) {
    return null;
  }

  if (tailHole && state.loadedTo !== null && !state.loading.initial) {
    return { kind: 'newer', after: state.loadedTo };
  }

  if (state.loadedTo === null) {
    // An empty topic has no bound to page from, so the summary is the probe.
    return state.lastId === null ? { kind: 'summary' } : { kind: 'initial', around: state.anchor };
  }

  // Reading history cannot be extended by paging the tail, so refresh metadata
  // only and leave the reader's window where they put it.
  return atEnd(state) ? { kind: 'newer', after: state.loadedTo } : { kind: 'summary' };
}
